import {
  LEAD_PAYMENT_METHOD_LABELS,
  LEAD_PRICE_RANGE_LABELS,
  Lead,
  LeadAlert,
  LeadPaymentMethod,
  LeadPriceRange,
} from '../../types';

export const onlyDigits = (s: string) => s.replace(/\D/g, '');

export const formatPhoneBR = (value: string) => {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export const getWhatsappLink = (phoneDigits: string, message: string) => {
  const safe = phoneDigits.replace(/\D/g, '');
  const withCountry = safe.length === 10 || safe.length === 11 ? `55${safe}` : safe;
  return `https://wa.me/${withCountry}?text=${encodeURIComponent(message)}`;
};

export const todayISODate = () => new Date().toISOString().slice(0, 10);

export const formatDateBR = (value?: string | null) => {
  if (!value) return '—';
  const d = value.slice(0, 10);
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return value;
  return `${day}/${m}/${y}`;
};

/** Dias sem contato a partir do último contato (ou primeiro contato). */
export const calcDaysWithoutContact = (lead: Pick<Lead, 'last_contact_at' | 'first_contact_at'>): number | null => {
  const ref = lead.last_contact_at || lead.first_contact_at;
  if (!ref) return null;
  const date = new Date(ref.slice(0, 10) + 'T12:00:00');
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const diff = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
};

/** Alerta efetivo: usa o salvo ou deriva dos dias sem contato. */
export const resolveLeadAlert = (lead: Pick<Lead, 'alert' | 'last_contact_at' | 'first_contact_at'>): LeadAlert => {
  if (lead.alert && lead.alert !== 'nenhum') return lead.alert;
  const days = calcDaysWithoutContact(lead);
  if (days == null) return lead.alert ?? 'nenhum';
  if (days >= 14) return 'urgente';
  if (days >= 7) return 'atencao';
  return lead.alert ?? 'nenhum';
};

export type LeadWhatsappFields = Pick<
  Lead,
  'name' | 'vehicle_interest' | 'price_range' | 'has_trade_in' | 'trade_in_vehicle' | 'payment_method'
>;

export const buildLeadWhatsappMessage = (lead: LeadWhatsappFields) => {
  const firstName = lead.name?.trim().split(/\s+/)[0];
  const greeting = firstName ? `Olá, ${firstName}!` : 'Olá!';
  const lines: string[] = [
    greeting,
    'Aqui é da loja. Vi seu cadastro e queria te atualizar sobre o que temos disponível.',
  ];

  const interest = lead.vehicle_interest?.trim();
  if (interest) {
    lines.push(`Sobre o ${interest}: temos opções alinhadas ao seu interesse.`);
  } else {
    lines.push('Temos opções alinhadas ao perfil que você buscou.');
  }

  const priceRange = lead.price_range
    ? LEAD_PRICE_RANGE_LABELS[lead.price_range as LeadPriceRange] ?? null
    : null;
  if (priceRange) {
    lines.push(
      `Na faixa de preço ${priceRange}, conseguimos te mostrar veículos que fazem sentido pro seu orçamento.`
    );
  } else {
    lines.push('Podemos te mostrar veículos na faixa de preço que você procura.');
  }

  if (lead.has_trade_in) {
    const trade = lead.trade_in_vehicle?.trim();
    lines.push(
      trade
        ? `Também podemos avaliar a troca do seu ${trade} para facilitar o negócio.`
        : 'Também podemos avaliar a troca do seu veículo atual para facilitar o negócio.'
    );
  }

  const payment = lead.payment_method
    ? LEAD_PAYMENT_METHOD_LABELS[lead.payment_method as LeadPaymentMethod] ?? null
    : null;
  if (payment) {
    lines.push(`Sobre pagamento (${payment}), te oriento nas melhores condições.`);
  }

  lines.push('Quando puder, me responde aqui que eu te envio as opções. Podemos conversar?');

  return lines.join('\n\n');
};
